import { Fragment } from 'react';

import type { getTagsOptions } from '@app/components/RecordModal/components/RecordDetails/utils';
import { Tag } from '@app/components/TagsSelect/ui/Tag';

import type {
  AppointmentPreviewField,
  AppointmentPreviewFieldKey,
} from '@pages/Calendar/data';

import type { FC, ReactNode } from 'react';

import '../styles/AppointmentCard.scss';

interface AppointmentCardContentProps {
  previewFields: AppointmentPreviewField[];
  services: string[];
  hiddenServicesCount: number;
  primaryText: string;
  secondaryText?: string;
  phone?: string;
  clientName?: string;
  tags: ReturnType<typeof getTagsOptions>;
  hiddenTagsCount: number;
  comment?: string;
}

// Rows of the appointment preview in the order chosen in the journal settings.
// Shared by the grid card and the mock preview in the settings, so both show
// exactly the same rows.
export const AppointmentCardContent: FC<AppointmentCardContentProps> = ({
  previewFields,
  services,
  hiddenServicesCount,
  primaryText,
  secondaryText,
  phone,
  clientName,
  tags,
  hiddenTagsCount,
  comment,
}) => {
  const renderRow = (key: AppointmentPreviewFieldKey): ReactNode => {
    switch (key) {
      case 'services':
        return services.length > 0 ? (
          <>
            {services.map((service, index) => (
              <div
                key={`${service}-${index}`}
                className="AppointmentCard__content-row AppointmentCard__service"
              >
                {service}
              </div>
            ))}

            {hiddenServicesCount > 0 && (
              <div className="AppointmentCard__content-row AppointmentCard__service AppointmentCard__service--more">
                +{hiddenServicesCount}
              </div>
            )}
          </>
        ) : (
          <>
            <div className="AppointmentCard__content-row AppointmentCard__title">
              {primaryText}
            </div>

            {secondaryText && (
              <div className="AppointmentCard__content-row AppointmentCard__subtitle">
                {secondaryText}
              </div>
            )}
          </>
        );
      case 'phone':
        return phone ? (
          <div className="AppointmentCard__content-row AppointmentCard__phone">
            {phone}
          </div>
        ) : null;
      case 'client':
        return clientName ? (
          <div className="AppointmentCard__content-row AppointmentCard__client-row">
            <div className="AppointmentCard__full-name">{clientName}</div>
          </div>
        ) : null;
      case 'tags':
        return tags.length > 0 ? (
          <div className="AppointmentCard__content-row AppointmentCard__tags">
            {tags.map(({ backgroundColor, color, value, label }) => (
              <Tag
                key={`${value}-${label}`}
                label={label}
                value={value}
                color={color}
                backgroundColor={backgroundColor}
                closable={false}
              />
            ))}

            {hiddenTagsCount > 0 && (
              <span className="AppointmentCard__tags-more">
                +{hiddenTagsCount}
              </span>
            )}
          </div>
        ) : null;
      case 'comment':
        return comment ? (
          <div className="AppointmentCard__content-row AppointmentCard__comment">
            {comment}
          </div>
        ) : null;
      default:
        return null;
    }
  };

  return (
    <div className="AppointmentCard__content">
      {previewFields
        .filter(({ isVisible }) => isVisible)
        .map(({ key }) => (
          <Fragment key={key}>{renderRow(key)}</Fragment>
        ))}
    </div>
  );
};
